/**
 * Phoenix Channel client for Erozion realtime events.
 *
 * Complete replacement for the old Socket.IO client (lib/socket.ts).
 * All stores should import exclusively from this file.
 */
import { Socket, Channel, Presence } from 'phoenix';
import { useAuthStore } from '../store/authStore';

const REALTIME_URL = import.meta.env.VITE_REALTIME_URL || 'wss://gateway.api.erozion.net/socket';

let socket: Socket | null = null;
const channels: Map<string, Channel> = new Map();
let presenceInstances: Map<string, Presence> = new Map();

// --- Socket lifecycle ---

export function connectRealtime(): Socket | null {
  if (typeof window === 'undefined') return null;
  if (socket) return socket;

  const token = localStorage.getItem('access_token');
  if (!token) {
    console.warn('[realtime] No access token, skipping connection');
    return null;
  }

  socket = new Socket(REALTIME_URL, {
    params: { token },
    reconnectAfterMs: (tries: number) => [1000, 2000, 5000, 10000][Math.min(tries - 1, 3)],
    heartbeatIntervalMs: 30000,
  });

  socket.onOpen(() => {
    console.log('[realtime] connected');
    const user = useAuthStore.getState().user;
    if (user) useAuthStore.getState().setOnlineStatus('online');
  });
  socket.onClose(() => console.log('[realtime] disconnected'));
  socket.onError((err: unknown) => console.error('[realtime] error', err));

  socket.connect();
  return socket;
}

export function disconnectRealtime(): void {
  channels.forEach((ch) => ch.leave());
  channels.clear();
  presenceInstances.clear();
  if (socket) {
    const user = useAuthStore.getState().user;
    if (user) useAuthStore.getState().setOnlineStatus('offline');
    socket.disconnect();
    socket = null;
  }
}

export function getRealtimeSocket(): Socket | null {
  if (!socket) {
    return connectRealtime();
  }
  return socket;
}

// Aliases matching the old socket.ts API names
export const initializeSocket = connectRealtime;
export const disconnectSocket = disconnectRealtime;
export const getSocket = getRealtimeSocket;

// --- Channel management ---

/**
 * Join a Phoenix channel topic. Returns the Channel instance.
 * If already joined, returns the existing channel.
 */
export function joinChannel(topic: string, params: Record<string, unknown> = {}): Channel | null {
  const sock = getRealtimeSocket();
  if (!sock) return null;

  const existing = channels.get(topic);
  if (existing) return existing;

  const channel = sock.channel(topic, params);
  channel
    .join()
    .receive('ok', () => console.log(`[realtime] joined ${topic}`))
    .receive('error', (resp: unknown) => console.error(`[realtime] join ${topic} failed`, resp));

  channels.set(topic, channel);
  return channel;
}

/**
 * Leave a Phoenix channel by topic.
 */
export function leaveChannel(topic: string): void {
  const ch = channels.get(topic);
  if (ch) {
    ch.leave();
    channels.delete(topic);
    presenceInstances.delete(topic);
  }
}

/**
 * Get an existing channel (must be joined first).
 */
export function getChannel(topic: string): Channel | null {
  return channels.get(topic) || null;
}

// --- Presence helpers ---

/**
 * Start tracking presence on a channel.
 * Returns a Presence instance that auto-syncs with server presence diffs.
 */
export function trackPresence(
  topic: string,
  onSync: (presences: Record<string, { metas: Array<Record<string, unknown>> }>) => void
): Presence | null {
  const ch = channels.get(topic);
  if (!ch) return null;

  const existing = presenceInstances.get(topic);
  if (existing) return existing;

  const presence = new Presence(ch);
  presence.onSync(() => {
    onSync(presence.list() as unknown as Record<string, { metas: Array<Record<string, unknown>> }>);
  });
  presenceInstances.set(topic, presence);
  return presence;
}

// --- Convenience helpers for common topics ---

export function joinUserChannel(userId: string): Channel | null {
  return joinChannel(`user:${userId}`);
}

export function joinServerChannel(serverId: string): Channel | null {
  return joinChannel(`server:${serverId}`);
}

export function joinChatChannel(channelId: string): Channel | null {
  return joinChannel(`channel:${channelId}`);
}

export function joinDMChannel(dmChannelId: string): Channel | null {
  return joinChannel(`dm:${dmChannelId}`);
}

export function leaveChatChannel(channelId: string): void {
  leaveChannel(`channel:${channelId}`);
}

export function leaveDMChannel(dmChannelId: string): void {
  leaveChannel(`dm:${dmChannelId}`);
}

export function joinVoiceChannelTopic(channelId: string): Channel | null {
  return joinChannel(`voice:${channelId}`);
}

export function leaveVoiceChannelTopic(channelId: string): void {
  leaveChannel(`voice:${channelId}`);
}

export function joinHostingChannel(serverId: string): Channel | null {
  return joinChannel(`hosting:${serverId}`);
}

export function leaveHostingChannel(serverId: string): void {
  leaveChannel(`hosting:${serverId}`);
}

function pushWithAck(channel: Channel, event: string, payload: Record<string, unknown>): Promise<any> {
  return new Promise((resolve, reject) => {
    channel
      .push(event, payload)
      .receive('ok', (resp: any) => resolve(resp))
      .receive('error', (resp: any) => reject(resp || new Error('socket push failed')))
      .receive('timeout', () => reject(new Error('socket push timeout')));
  });
}

// --- Compat wrappers matching old socket.ts function signatures ---

/** Join a server room — alias for joining `server:{id}` */
export function joinServer(serverId: string): Channel | null {
  return joinServerChannel(serverId);
}

/** Join a chat channel room — same as joinChatChannel but matches old socket.ts name */
export function joinChannelRoom(channelId: string): Channel | null {
  return joinChatChannel(channelId);
}

/** Join a DM room — same as joinDMChannel but matches old socket.ts name */
export function joinDirectMessage(otherUserId: string): Channel | null {
  return joinDMChannel(otherUserId);
}

// --- Messaging ---

export function sendChannelMessage(
  content: string,
  channelId: string,
  replyTo?: string | null,
  serverId?: string,
): void {
  if (!channelId) {
    console.error('[realtime] cannot send: channelId is empty');
    return;
  }
  let ch = getChannel(`channel:${channelId}`);
  if (!ch) {
    ch = joinChatChannel(channelId);
  }
  if (!ch) {
    console.error(`[realtime] cannot send: channel:${channelId} not joined`);
    return;
  }
  ch.push('send_message', { content, reply_to: replyTo ?? null, server_id: serverId ?? '' });
}

export function sendDirectMessage(
  content: string,
  recipientId: string,
): void {
  const ch = getChannel(`dm:${recipientId}`);
  if (!ch) {
    console.error(`[realtime] cannot send DM: dm:${recipientId} not joined`);
    return;
  }
  ch.push('send_message', { content, recipient_id: recipientId });
}

export function editMessage(
  messageId: string,
  content: string,
  isDm: boolean = false,
  topicId?: string,
  serverId?: string,
): void {
  if (topicId) {
    const ch = getChannel(`${isDm ? 'dm' : 'channel'}:${topicId}`);
    if (ch) {
      ch.push('edit_message', { message_id: messageId, content, server_id: serverId ?? '' });
      return;
    }
  }
  // Push to any currently-joined channel/dm topic
  for (const [t, ch] of channels) {
    if (isDm && t.startsWith('dm:')) {
      ch.push('edit_message', { message_id: messageId, content });
      return;
    }
    if (!isDm && t.startsWith('channel:')) {
      ch.push('edit_message', { message_id: messageId, content, server_id: serverId ?? '' });
      return;
    }
  }
  console.error('[realtime] no joined channel to route edit_message');
}

export function deleteMessage(
  messageId: string,
  isDm: boolean = false,
  topicId?: string,
  serverId?: string,
): void {
  if (topicId) {
    const ch = getChannel(`${isDm ? 'dm' : 'channel'}:${topicId}`);
    if (ch) {
      ch.push('delete_message', { message_id: messageId, server_id: serverId ?? '' });
      return;
    }
  }
  for (const [t, ch] of channels) {
    if (isDm && t.startsWith('dm:')) {
      ch.push('delete_message', { message_id: messageId });
      return;
    }
    if (!isDm && t.startsWith('channel:')) {
      ch.push('delete_message', { message_id: messageId, server_id: serverId ?? '' });
      return;
    }
  }
  console.error('[realtime] no joined channel to route delete_message');
}

export function addReaction(
  messageId: string,
  emoji: string,
  isDm: boolean = false,
  topicId?: string,
): void {
  if (topicId) {
    const ch = getChannel(`${isDm ? 'dm' : 'channel'}:${topicId}`);
    if (ch) {
      ch.push('add_reaction', { message_id: messageId, emoji });
      return;
    }
  }
  for (const [t, ch] of channels) {
    if (isDm && t.startsWith('dm:')) {
      ch.push('add_reaction', { message_id: messageId, emoji });
      return;
    }
    if (!isDm && t.startsWith('channel:')) {
      ch.push('add_reaction', { message_id: messageId, emoji });
      return;
    }
  }
}

export function removeReaction(
  messageId: string,
  emoji: string,
  isDm: boolean = false,
  topicId?: string,
): void {
  if (topicId) {
    const ch = getChannel(`${isDm ? 'dm' : 'channel'}:${topicId}`);
    if (ch) {
      ch.push('remove_reaction', { message_id: messageId, emoji });
      return;
    }
  }
  for (const [t, ch] of channels) {
    if (isDm && t.startsWith('dm:')) {
      ch.push('remove_reaction', { message_id: messageId, emoji });
      return;
    }
    if (!isDm && t.startsWith('channel:')) {
      ch.push('remove_reaction', { message_id: messageId, emoji });
      return;
    }
  }
}

// --- Typing indicators ---

export function startTyping(
  channelId: string,
  isDm: boolean = false,
  recipientId?: string,
): void {
  const topic = isDm && recipientId ? `dm:${recipientId}` : `channel:${channelId}`;
  const ch = getChannel(topic);
  if (ch) ch.push('typing_start', {});
}

export function stopTyping(
  channelId: string,
  isDm: boolean = false,
  recipientId?: string,
): void {
  const topic = isDm && recipientId ? `dm:${recipientId}` : `channel:${channelId}`;
  const ch = getChannel(topic);
  if (ch) ch.push('typing_stop', {});
}

// --- Push-with-ack message helpers (for stores that need the response) ---

export async function sendChannelSocketMessage(
  channelId: string,
  payload: { content: string; server_id?: string; reply_to?: string | null; client_msg_id?: string }
): Promise<any> {
  const topic = `channel:${channelId}`;
  const channel = getChannel(topic);
  if (!channel) {
    throw new Error('channel not joined');
  }
  return pushWithAck(channel, 'send_message', payload);
}

export async function sendDMSocketMessage(
  dmChannelId: string,
  payload: { content: string; recipient_id: string; client_msg_id?: string }
): Promise<any> {
  const topic = `dm:${dmChannelId}`;
  const channel = getChannel(topic);
  if (!channel) {
    throw new Error('dm channel not joined');
  }
  return pushWithAck(channel, 'send_message', payload);
}

// --- DM Call signaling ---

/**
 * Push a call_invite to the callee via the caller's own user channel.
 * The Elixir user_channel.ex will broadcast call_incoming to the callee.
 */
export function sendCallInvite(
  selfUserId: string,
  toUserId: string,
  displayName: string,
  avatarUrl: string
): void {
  const ch = getChannel(`user:${selfUserId}`);
  if (!ch) return;
  ch.push('call_invite', { to_user_id: toUserId, display_name: displayName, avatar_url: avatarUrl });
}

/**
 * Push a call_cancel to stop ringing on the callee's side.
 */
export function sendCallCancel(selfUserId: string, toUserId: string): void {
  const ch = getChannel(`user:${selfUserId}`);
  if (!ch) return;
  ch.push('call_cancel', { to_user_id: toUserId });
}

/**
 * Wire call_incoming / call_cancelled listeners on the user's own channel.
 * Must be called after joinUserChannel(userId).
 */
export function setupCallSignaling(
  userId: string,
  onIncoming: (payload: { from_user_id: string; display_name: string; avatar_url: string }) => void,
  onCancelled: (payload: { from_user_id: string }) => void
): void {
  const ch = getChannel(`user:${userId}`);
  if (!ch) return;
  ch.on('call_incoming', onIncoming);
  ch.on('call_cancelled', onCancelled);
}

// --- Voice channel push helpers ---

export function voicePush(channelId: string, event: string, payload: Record<string, unknown> = {}): void {
  const ch = getChannel(`voice:${channelId}`);
  if (ch) ch.push(event, payload);
}

// --- Hosting push helpers ---

export function hostingPush(serverId: string, event: string, payload: Record<string, unknown> = {}): void {
  const ch = getChannel(`hosting:${serverId}`);
  if (ch) ch.push(event, payload);
}

// --- User channel push helper ---

export function userPush(userId: string, event: string, payload: Record<string, unknown> = {}): void {
  const ch = getChannel(`user:${userId}`);
  if (ch) ch.push(event, payload);
}

// Re-export Channel type for stores that need it
export type { Channel };
